package org.moxultimate.topwithmox.navigation

import androidx.compose.runtime.*
import kotlinx.browser.window
import org.moxultimate.topwithmox.apps.data.AppsResponse
import org.moxultimate.topwithmox.product.data.ProductContentResponse
import org.moxultimate.topwithmox.shows.data.ShowsResponse
import org.moxultimate.topwithmox.tech.data.TechResponse
import org.moxultimate.topwithmox.wallpaper.data.WallpaperResponse


// JS interop
external fun encodeURIComponent(value: String): String


// NavParams — shared query-parameter key constants (avoids typos at call sites)
object NavParams {
    const val ID             = "id"
    const val TITLE          = "title"
    const val SOURCE         = "source"
    const val DESCRIPTION    = "description"
    const val SERIES         = "series"
    const val TYPE           = "type"
    const val IMAGE_PATH     = "imagePath"
    const val THUMBNAIL_PATH = "thumbnailPath"
    const val PANORAMA_PATH  = "panoramaPath"
    const val VIDEO_LINK     = "videoLink"
    const val CREATED_AT     = "createdAt"
    const val TAGS       = "tags"
    const val RESOLUTION = "resolution"
    const val CONTENT_ID    = "contentId"
    const val PRODUCT_ID    = "productId"
    const val PANORAMA_SRC  = "src"
}

// NavEntry — a single backstack record
data class NavEntry(
    val path: String,
    val params: Map<String, String> = emptyMap(),
)

// AppNavigator — manages the in-memory backstack and wraps the Kobweb router
class AppNavigator internal constructor(
    private val routeTo: (String) -> Unit,
) {
    private val _stack = mutableStateListOf<NavEntry>()

    /** Read-only view of the navigation history (oldest → newest). */
    val backStack: List<NavEntry> get() = _stack

    /** `true` when there is at least one previous entry to return to. */
    val canGoBack: Boolean get() = _stack.size > 1

    // ── Core navigation primitives ────────────────────────────────────────

    /**
     * Push a new [route] with optional [params] onto the backstack and navigate.
     *
     * Usage:
     * ```kotlin
     * navigator.navigate(NavRoute.ABOUT)
     * navigator.navigate(NavRoute.PROFILE, mapOf(NavParams.ID to "42"))
     * ```
     */
    fun navigate(route: String, params: Map<String, String> = emptyMap()) {
        val path = NavRoute.build(route, params)
        _stack += NavEntry(path, params)
        routeTo(path)
    }

    /**
     * Navigate to [route] replacing the current backstack entry instead of
     * pushing a new one. Useful for "redirect" flows where you don't want the
     * user to be able to press Back and return to the replaced screen.
     */
    fun replace(route: String, params: Map<String, String> = emptyMap()) {
        val path = NavRoute.build(route, params)
        val entry = NavEntry(path, params)
        if (_stack.isEmpty()) _stack += entry else _stack[_stack.lastIndex] = entry
        routeTo(path)
    }

    /**
     * Pop the current entry and navigate to the previous one.
     * Does nothing when [canGoBack] is `false`.
     */
    fun goBack() {
        if (!canGoBack) return
        _stack.removeAt(_stack.lastIndex)
        routeTo(_stack.last().path)
    }

    /**
     * Discard the entire backstack and navigate to [route] as a fresh root.
     * Defaults to [NavRoute.DASHBOARD] when no route is specified.
     */
    fun clearAndNavigate(route: String = NavRoute.DASHBOARD) {
        _stack.clear()
        navigate(route)
    }

    // ── Named navigators — add one for every new route ────────────────────
    // Calling a named navigator is preferred over calling navigate() directly
    // because it keeps route strings out of feature code.

    fun goToDashboard() = navigate(NavRoute.DASHBOARD)
    fun goToAbout()   = navigate(NavRoute.ABOUT)
    fun goToArchitecturePage() = navigate(NavRoute.ARCHITECTURE_PAGE)
    fun goToWallpaperPage()   = navigate(NavRoute.WALLPAPER)
    fun goToWallpaperCRUD()   = navigate(NavRoute.WALLPAPER_CRUD)
    fun goToProductsPage()    = navigate(NavRoute.PRODUCTS)

    fun goToProductDetailPage(contentId: Long) {
        val url = NavRoute.build(NavRoute.PRODUCT_DETAIL, mapOf(NavParams.CONTENT_ID to contentId.toString()))
        window.open(url, "_blank")
    }
    fun goToAppsPage()        = navigate(NavRoute.APPS)
    fun goToAppsCRUD()         = navigate(NavRoute.APPS_CRUD)
    fun goToAppsAddPage()      = window.open(NavRoute.APPS_ADD, "_blank")

    fun goToAppsUpdatePage(app: AppsResponse) {
        val url = NavRoute.build(
            NavRoute.APPS_UPDATE,
            mapOf(
                NavParams.ID          to app.id.toString(),
                NavParams.TITLE       to app.title.orEmpty(),
                NavParams.TYPE        to app.type.orEmpty(),
                NavParams.DESCRIPTION to app.description.orEmpty(),
                NavParams.IMAGE_PATH  to app.imagePath.orEmpty(),
                NavParams.VIDEO_LINK  to app.videoLink.orEmpty(),
                NavParams.TAGS        to app.tags.orEmpty(),
            )
        )
        window.open(url, "_blank")
    }

    fun goToShowsPage()        = navigate(NavRoute.SHOWS)
    /**
     * goToPanoramaPage
     * Opens the 360° panorama viewer in a new tab.
     *
     * @param src URL of the 360° image to display; pass empty string to show the default placeholder.
     */
    fun goToPanoramaPage(src: String = "") = window.open(NavRoute.panorama(src), "_blank")
    fun goToShowsCRUD()        = navigate(NavRoute.SHOWS_CRUD)
    fun goToShowsAddPage()     = window.open(NavRoute.SHOWS_ADD, "_blank")

    fun goToShowsUpdatePage(show: ShowsResponse) {
        val url = NavRoute.build(
            NavRoute.SHOWS_UPDATE,
            mapOf(
                NavParams.ID          to show.id.toString(),
                NavParams.TITLE       to show.title.orEmpty(),
                NavParams.TYPE        to show.type.orEmpty(),
                NavParams.TAGS        to show.tags.orEmpty(),
                NavParams.DESCRIPTION to show.description.orEmpty(),
                NavParams.THUMBNAIL_PATH to show.thumbnailPath.orEmpty(),
                NavParams.VIDEO_LINK  to show.videoLink.orEmpty(),
            )
        )
        window.open(url, "_blank")
    }

    fun goToProductCRUD()     = navigate(NavRoute.PRODUCT_CRUD)
    fun goToProductAddPage()  = window.open(NavRoute.PRODUCT_ADD, "_blank")

    fun goToProductUpdatePage(content: ProductContentResponse) {
        val url = NavRoute.build(
            NavRoute.PRODUCT_UPDATE,
            mapOf(
                NavParams.ID             to content.id.toString(),
                NavParams.TITLE          to content.title,
                NavParams.DESCRIPTION    to content.description.orEmpty(),
                NavParams.VIDEO_LINK     to content.videoLink.orEmpty(),
                NavParams.TAGS           to content.tags.orEmpty(),
                NavParams.TYPE           to content.type.orEmpty(),
                NavParams.THUMBNAIL_PATH to content.thumbnailPath.orEmpty(),
            )
        )
        window.open(url, "_blank")
    }

    fun goToProductItemAddPage(contentId: Long) {
        val url = NavRoute.build(
            NavRoute.PRODUCT_ITEM_ADD,
            mapOf(NavParams.CONTENT_ID to contentId.toString()),
        )
        window.open(url, "_blank")
    }

    fun goToProductItemUpdatePage(contentId: Long, productId: Long) {
        val url = NavRoute.build(
            NavRoute.PRODUCT_ITEM_UPDATE,
            mapOf(
                NavParams.CONTENT_ID to contentId.toString(),
                NavParams.PRODUCT_ID to productId.toString(),
            ),
        )
        window.open(url, "_blank")
    }

    fun goToTechPage() = navigate(NavRoute.TECH)
    fun goToTechCRUD() = navigate(NavRoute.TECH_CRUD)
    fun goToTechAddPage() = window.open(NavRoute.TECH_ADD, "_blank")

    /**
     * goToTechUpdatePage
     * Opens the tech update page in a new tab with all tech fields encoded as query params.
     *
     * @param tech TechResponse whose fields are encoded into the URL for the update page.
     */
    fun goToTechUpdatePage(tech: TechResponse) {
        val url = NavRoute.build(
            NavRoute.TECH_UPDATE,
            mapOf(
                NavParams.ID             to tech.id.toString(),
                NavParams.TITLE          to tech.title.orEmpty(),
                NavParams.TYPE           to tech.type.orEmpty(),
                NavParams.TAGS           to tech.tags.orEmpty(),
                NavParams.DESCRIPTION    to tech.description.orEmpty(),
                NavParams.THUMBNAIL_PATH to tech.thumbnailPath.orEmpty(),
                NavParams.VIDEO_LINK     to tech.videoLink.orEmpty(),
            )
        )
        window.open(url, "_blank")
    }

    fun goToWallpaperAddPage() = window.open(NavRoute.WALLPAPER_ADD, "_blank")

    fun goToWallpaperDetailsPage(wallpaperResponse: WallpaperResponse) {
        val url = NavRoute.build(
            NavRoute.WALLPAPER_DETAILS,
            mapOf(NavParams.ID to wallpaperResponse.id.toString())
        )
        window.open(url, "_blank")
    }

    fun goToWallpaperUpdatePage(wallpaper: WallpaperResponse) {
        val url = NavRoute.build(
            NavRoute.WALLPAPER_UPDATE,
            mapOf(
                NavParams.ID             to wallpaper.id.toString(),
                NavParams.TITLE          to wallpaper.name,
                NavParams.DESCRIPTION    to wallpaper.description.orEmpty(),
                NavParams.SERIES         to wallpaper.series,
                NavParams.TYPE           to wallpaper.type,
                NavParams.IMAGE_PATH     to wallpaper.imagePath.orEmpty(),
                NavParams.THUMBNAIL_PATH to wallpaper.thumbnailPath.orEmpty(),
                NavParams.PANORAMA_PATH  to wallpaper.panoramaPath.orEmpty(),
                NavParams.VIDEO_LINK     to wallpaper.videoLink.orEmpty(),
                NavParams.CREATED_AT     to wallpaper.createdAt.orEmpty(),
                NavParams.TAGS           to wallpaper.tags,
                NavParams.RESOLUTION     to wallpaper.resolution,
            )
        )
        window.open(url, "_blank")
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// CompositionLocal + provider
// ─────────────────────────────────────────────────────────────────────────────

val LocalAppNavigator = staticCompositionLocalOf<AppNavigator> {
    error("AppNavigator not provided. Wrap your layout with ProvideAppNavigator { }.")
}

@Composable
fun ProvideAppNavigator(routeTo: (String) -> Unit, content: @Composable () -> Unit) {
    val navigator = remember { AppNavigator(routeTo) }
    CompositionLocalProvider(LocalAppNavigator provides navigator) {
        content()
    }
}

@Composable
fun rememberAppNavigator(): AppNavigator = LocalAppNavigator.current
